'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { COOKIE_PREFERENCES_HASH } from '@/constants/legalRoutes';
import { CONSENT_CATEGORIES, type ConsentCategory } from '@/lib/consent/constants';
import { readConsentState, writeConsentState } from '@/lib/consent/storage';
import {
  acceptAllChoices,
  defaultConsentState,
  rejectAllChoices,
  type ConsentChoices,
  type ConsentState,
} from '@/lib/consent/types';

type ConsentContextValue = {
  ready: boolean;
  state: ConsentState;
  preferencesOpen: boolean;
  hasConsent: (category: ConsentCategory) => boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  saveCustom: (choices: Pick<ConsentChoices, 'externalContent'>) => void;
  openPreferences: () => void;
  closePreferences: () => void;
};

const ConsentContext = createContext<ConsentContextValue | null>(null);

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [state, setState] = useState<ConsentState>(defaultConsentState);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  useEffect(() => {
    setState(readConsentState());
    setReady(true);
  }, []);

  useEffect(() => {
    const syncFromHash = () => {
      if (window.location.hash === COOKIE_PREFERENCES_HASH) {
        setPreferencesOpen(true);
      }
    };
    syncFromHash();
    window.addEventListener('hashchange', syncFromHash);
    return () => window.removeEventListener('hashchange', syncFromHash);
  }, []);

  const persist = useCallback((choices: ConsentChoices) => {
    const next = writeConsentState(choices);
    setState(next);
  }, []);

  const acceptAll = useCallback(() => {
    persist(acceptAllChoices());
    setPreferencesOpen(false);
  }, [persist]);

  const rejectAll = useCallback(() => {
    persist(rejectAllChoices());
    setPreferencesOpen(false);
  }, [persist]);

  const saveCustom = useCallback(
    (choices: Pick<ConsentChoices, 'externalContent'>) => {
      persist({
        necessary: true,
        externalContent: choices.externalContent,
      });
      setPreferencesOpen(false);
    },
    [persist],
  );

  const openPreferences = useCallback(() => {
    setPreferencesOpen(true);
  }, []);

  const closePreferences = useCallback(() => {
    setPreferencesOpen(false);
    if (window.location.hash === COOKIE_PREFERENCES_HASH) {
      history.replaceState(
        null,
        '',
        `${window.location.pathname}${window.location.search}`,
      );
    }
  }, []);

  const hasConsent = useCallback(
    (category: ConsentCategory) => {
      if (category === CONSENT_CATEGORIES.necessary) {
        return true;
      }
      if (!state.hasResponded) {
        return false;
      }
      return Boolean(state.choices[category]);
    },
    [state],
  );

  const value = useMemo<ConsentContextValue>(
    () => ({
      ready,
      state,
      preferencesOpen,
      hasConsent,
      acceptAll,
      rejectAll,
      saveCustom,
      openPreferences,
      closePreferences,
    }),
    [
      ready,
      state,
      preferencesOpen,
      hasConsent,
      acceptAll,
      rejectAll,
      saveCustom,
      openPreferences,
      closePreferences,
    ],
  );

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
}

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error('useConsent must be used within ConsentProvider');
  }
  return ctx;
}

/** Safe hook for components that may render outside provider in tests */
export function useConsentOptional(): ConsentContextValue | null {
  return useContext(ConsentContext);
}
